import { IDocumentFolderModel } from '@api/models';
import { moveDocumentFolder } from '@api/services/document.service';
import { useMemo } from 'react';
import MoveItemDrawer from '../MoveItemDrawer/MoveItemDrawer';

interface MoveFolderDrawerProps {
    clientId: string;
    folders: IDocumentFolderModel[];
    selectedFolder: IDocumentFolderModel;
    show: boolean;
    onClose: () => void;
    refetchAll: () => void;
}

const MoveFolderDrawer = ({ clientId, show, folders, onClose, selectedFolder, refetchAll }: MoveFolderDrawerProps) => {
    const currentLocationParentId = useMemo(
        () => (selectedFolder?.parentId ?? null) as string | null,
        [selectedFolder]
    );

    const excludeFolderIds = useMemo(() => {
        const set = new Set<string>();

        if (!selectedFolder?._id) return set;

        set.add(selectedFolder._id);

        const childrenByParent = new Map<string, IDocumentFolderModel[]>();

        folders.forEach((f) => {
            if (!f.parentId) return;

            if (!childrenByParent.has(f.parentId as string)) {
                childrenByParent.set(f.parentId as string, []);
            }

            childrenByParent.get(f.parentId as string)!.push(f);
        });

        const stack = [selectedFolder._id];

        while (stack.length) {
            const id = stack.pop()!;
            const children = childrenByParent.get(id) ?? [];

            for (const child of children) {
                if (!set.has(child._id)) {
                    set.add(child._id);
                    stack.push(child._id);
                }
            }
        }

        return set;
    }, [folders, selectedFolder]);

    return (
        <MoveItemDrawer
            show={show}
            onClose={onClose}
            refetchAll={refetchAll}
            title="Move Folder"
            itemName={selectedFolder?.name}
            folders={folders}
            initialLocation={currentLocationParentId}
            currentLocationParentId={currentLocationParentId}
            initialBrowseParentId={currentLocationParentId}
            highlightFolderId={selectedFolder?._id}
            highlightLabel="This folder"
            excludeFolderIds={excludeFolderIds}
            successMessage="Folder moved successfully."
            onMove={(destinationParentId) =>
                moveDocumentFolder(clientId, selectedFolder?._id, {
                    parentId: destinationParentId,
                })
            }
        />
    );
};

export default MoveFolderDrawer;
